import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { parseDate, calculateEndDate } from '../utils/dateUtils';
import { getServiceTimeBreakdown } from '../utils/serviceTime';
import { formatServiceText, formatWorkOrderText, minutesToHours, normalizeProgress, getStatusDisplay, getStatusColor } from '../utils/formatUtils';
import { getProgressStatus, getWorkOrderStatus, isScheduledOnly } from '../utils/statusUtils';
import { getStatusEmoji } from '../utils/formatUtils';
import { sortServicesByScheduledDate } from '../utils/workOrderGroups';

export const useGanttTransform = (workOrderGroups) => {
	const { t } = useTranslation();
	const ganttData = useMemo(() => {
		if (!workOrderGroups || !Array.isArray(workOrderGroups)) return [];

		const tasks = [];
		let taskId = 1;

		// Calculate earliest date for each work order group
		workOrderGroups.forEach((workOrderGroup) => {
			const services = workOrderGroup.services || [];
			workOrderGroup.earliestDate = services.reduce((earliest, service) => {
				const serviceDate = parseDate(service.scheduledDate);
				if (!serviceDate) return earliest;
				return !earliest || serviceDate < earliest ? serviceDate : earliest;
			}, null);
		});

		workOrderGroups.forEach((workOrderGroup) => {
			const workOrderId = taskId++;
			const allServices = sortServicesByScheduledDate(workOrderGroup.services);

			// The group's own scheduledDate is what the server last sent, so a
			// service moved earlier would leave the bar starting after it
			const workOrderStartDate =
				workOrderGroup.earliestDate || parseDate(workOrderGroup.scheduledDate);
			
			const latestEndDate = allServices.reduce((latest, service) => {
				const serviceEndDate = calculateEndDate(
					service.scheduledDate,
					getServiceTimeBreakdown(service).total
				);
				if (!serviceEndDate) return latest;
				return !latest || serviceEndDate > latest ? serviceEndDate : latest;
			}, workOrderStartDate);

			const workOrderProgress = normalizeProgress(workOrderGroup.percentageComplete ?? 0);
			const totalMinutes = workOrderGroup.estimatedMinutes || 0;
			const totalHours = minutesToHours(totalMinutes);
			const woInterval = workOrderGroup.interval || "";
			const workOrderStatus = getWorkOrderStatus(workOrderGroup);
			const canReschedule = isScheduledOnly(allServices);

			// Create work order parent task
			tasks.push({
				id: workOrderId,
				text: formatWorkOrderText(workOrderGroup),
				workOrder: workOrderGroup.workOrder,
				modelName: workOrderGroup.modelName || "",
				model: workOrderGroup.model || "",
				interval: woInterval,
				status: "", // Work orders don't have individual status emojis
				progress: workOrderProgress,
				percComplete: `${workOrderProgress}%`,
				start: workOrderStartDate || new Date(),
				end: latestEndDate || new Date(),
				estimatedMinutes: totalHours,
				// The built-in summary type is what makes the library stretch the
				// work order bar around its services while one of them is dragged
				type: "summary",
				statusColor: workOrderStatus,
				statusTextColor: getStatusColor(workOrderGroup.percentageOverTime || 0),
				open: true,
			});

			// Create service tasks
			allServices.forEach((service) => {
				const storedStart = parseDate(service.scheduledDate);
				const startDate = storedStart || new Date();
				const timeBreakdown = getServiceTimeBreakdown(service);
				// The bar spans estimated and actual time so the unused
				// remainder or the overrun can both be drawn on the one line
				const endDate = calculateEndDate(service.scheduledDate, timeBreakdown.total) || startDate;
				const progressStatus = getProgressStatus(service);
				const progressField = service.percentageComplete ?? 0;
				const normalizedProgress = normalizeProgress(progressField);
				const serviceHours = minutesToHours(timeBreakdown.estimated);

				tasks.push({
					id: taskId++,
					text: formatServiceText(service),
					workOrder: workOrderGroup.workOrder,
					modelName: workOrderGroup.modelName || "",
					model: workOrderGroup.model || "",
					interval: service.interval || "",
					status: getStatusDisplay(service.status || "S", t),
					progress: normalizedProgress,
					percComplete: `${normalizedProgress}%`,
					start: startDate,
					end: endDate,
					estimatedMinutes: serviceHours,
					parent: workOrderId,
					type: `role-${progressStatus}`,
					statusCode: service.status || "S",
					icon: getStatusEmoji(service.status || "S"),
					role: service.role || "Role",
					statusColor: progressStatus,
					statusTextColor: getStatusColor(service.percentageOverTime || 0),
					timeBreakdown,
					// A bar drawn at "now" for want of a stored date has nothing to
					// revert to, so it stays put
					isDraggable: canReschedule && Boolean(storedStart),
					serviceData: service
				});
			});
		});

		return tasks;
	}, [workOrderGroups, t]);

	const taskTypes = useMemo(() => [
		// summary must stay registered or the library falls back to the task
		// CSS class and the work order bars lose their wx-summary styling
		{ id: "summary", label: "Work Order" },
		{ id: "role-ontrack", label: "Role - On Track" },
		{ id: "role-slight-over", label: "Role - ≤5% Over Time" },
		{ id: "role-moderate-over", label: "Role - 5-10% Over Time" },
		{ id: "role-severe-over", label: "Role - >10% Over Time" },
	], []);

	return { ganttData, taskTypes };
};